import {Trend} from 'platform/components';
import {useFormatCurrency, useFormatNumber} from 'platform/locale';

import {isNil, isNotNil} from 'ramda';

import {SalesLeagueLeaderboardResponse} from '@dms/api/sales';
import i18n from '@dms/i18n';

import {EMPTY_PLACEHOLDER, Nullish, RequiredTestIdProps} from 'shared/web';

import {SalesLeagueKpiItem, SalesLeagueKpiPanel} from '../../../components/SalesLeagueKpiPanel';
import {
  SALES_LEAGUE_KPI_AVG_MARGIN_COLOR,
  SALES_LEAGUE_KPI_BEST_MARGIN_COLOR,
  SALES_LEAGUE_KPI_TOTAL_MARGIN_COLOR,
  SALES_LEAGUE_KPI_VEHICLES_COLOR,
} from '../../../constants/SALES_LEAGUE_KPI_ACCENT_COLORS';
import {formatMarginAmount} from '../../../utils/formatMarginAmount';
import {formatVehiclesChangeLabel} from '../../../utils/formatVehiclesChangeLabel';
import {parseRatio} from '../../../utils/parseRatio';
import {getSalesLeagueRepName} from '../utils/getSalesLeagueRepName';

const NO_CHANGE = 0;
const VEHICLES_PER_REP_DECIMALS = 1;

interface SalesLeagueBoardKpisProps extends RequiredTestIdProps {
  leaderboard: SalesLeagueLeaderboardResponse;
  periodLabel: string;
}

/**
 * Three of the four tiles are margin, so a viewer without the margin permission would be
 * left with a single card. Two figures that need no margin stand in for them instead —
 * the row keeps its shape and the viewer keeps everything they are allowed to see.
 */
export function SalesLeagueBoardKpis(props: SalesLeagueBoardKpisProps) {
  const formatCurrency = useFormatCurrency();
  const formatNumber = useFormatNumber();

  const formatMargin = (amount: string | Nullish) =>
    formatMarginAmount(amount, props.leaderboard.currency, formatCurrency);

  const totals = props.leaderboard.totals;

  // The rep holding the highest per-vehicle margin. The backend resolves the maximum and
  // its tie-break, so this is only a name lookup — never re-scan the rows for it.
  const bestMarginRep = props.leaderboard.rows.find(
    (row) => row.userId === totals.bestMarginUserId
  );

  // The leaderboard reports no previous margin, only previous vehicles, so the comparison
  // is an absolute count. It also survives a previous window of zero, which a percentage
  // does not.
  const vehiclesSoldChange = totals.vehiclesSoldChange;

  const vehiclesPerRep = parseRatio(totals.vehiclesPerSalesRep);

  const totalVehiclesSoldCard: SalesLeagueKpiItem = {
    id: 'totalVehiclesSold',
    accentColor: SALES_LEAGUE_KPI_VEHICLES_COLOR,
    label: i18n.t('page.salesLeague.kpi.totalVehiclesSold'),
    value: formatNumber(totals.vehiclesSold),
    unit: i18n.t('page.salesLeague.kpi.totalVehiclesSoldUnit'),
    footer: i18n.t('page.salesLeague.kpi.vsPreviousPeriod'),
    changeChip: (
      <Trend
        label={formatVehiclesChangeLabel(vehiclesSoldChange, formatNumber)}
        isPositive={vehiclesSoldChange > NO_CHANGE}
        isNegative={vehiclesSoldChange < NO_CHANGE}
        isSubtle
      />
    ),
  };

  const totalMarginCard: SalesLeagueKpiItem = {
    id: 'totalMargin',
    accentColor: SALES_LEAGUE_KPI_TOTAL_MARGIN_COLOR,
    label: i18n.t('entity.order.labels.totalMargin'),
    value: formatMargin(totals.totalMargin),
    footer: i18n.t('page.salesLeague.kpi.totalVehiclesSoldFooter', {
      period: props.periodLabel,
      count: formatNumber(totals.salesReps) ?? EMPTY_PLACEHOLDER,
    }),
  };

  const avgMarginCard: SalesLeagueKpiItem = {
    id: 'avgMargin',
    accentColor: SALES_LEAGUE_KPI_AVG_MARGIN_COLOR,
    label: i18n.t('page.salesLeague.kpi.avgMarginPerVehicle'),
    value: formatMargin(totals.avgMarginPerVehicle),
    footer: i18n.t('page.salesLeague.kpi.avgMarginFooter'),
  };

  const bestMarginCard: SalesLeagueKpiItem = {
    id: 'bestMargin',
    accentColor: SALES_LEAGUE_KPI_BEST_MARGIN_COLOR,
    label: i18n.t('page.salesLeague.kpi.bestMargin'),
    value: formatMargin(totals.bestMarginPerVehicle),
    footer: isNotNil(bestMarginRep) ? getSalesLeagueRepName(bestMarginRep) : EMPTY_PLACEHOLDER,
  };

  const repsWithSalesCard: SalesLeagueKpiItem = {
    id: 'repsWithSales',
    accentColor: SALES_LEAGUE_KPI_TOTAL_MARGIN_COLOR,
    label: i18n.t('page.salesLeague.kpi.repsWithSales'),
    value: formatNumber(totals.salesReps),
    footer: props.periodLabel,
  };

  const vehiclesPerRepCard: SalesLeagueKpiItem = {
    id: 'vehiclesPerRep',
    accentColor: SALES_LEAGUE_KPI_AVG_MARGIN_COLOR,
    label: i18n.t('page.salesLeague.kpi.vehiclesPerRep'),
    value: isNil(vehiclesPerRep)
      ? undefined
      : formatNumber(vehiclesPerRep, VEHICLES_PER_REP_DECIMALS),
    footer: props.periodLabel,
  };

  const cards: SalesLeagueKpiItem[] = props.leaderboard.marginVisible
    ? [totalVehiclesSoldCard, avgMarginCard, totalMarginCard, bestMarginCard]
    : [totalVehiclesSoldCard, repsWithSalesCard, vehiclesPerRepCard];

  return <SalesLeagueKpiPanel items={cards} data-testid={props['data-testid']} />;
}
